import * as React from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { AlertCircle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

// -----------------------------------------------------------------------------
// Toasts, matching the mobile app's `ToastCard`.
//
// The React Native version is a small frosted pill that hugs its own content and
// sits top-right - closer to a native macOS/iOS notification than a full-width
// Android snackbar. This reproduces it on the web so the two surfaces feel like
// one product: 90%-white over a backdrop blur, a hairline border, a 24px tinted
// icon chip, a 12.5px bold title over an 11px secondary message, and no close
// button. Tapping anywhere on it dismisses, exactly as it does on the phone.
//
// TWO DELIBERATE DIVERGENCES from the mobile card:
//
//   1. `rounded-[14px]`, above this panel's 6px ceiling. The ceiling exists so
//      dense data chrome stays sharp; a floating notification is not that, and
//      matching the app was the explicit ask.
//   2. Error toasts do NOT auto-dismiss. On the phone every toast times out;
//      here a failed approval or refund that vanishes while the operator looks
//      away is a write they will believe succeeded.
// -----------------------------------------------------------------------------

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export type ToastPayload = {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  /** Copyable correlation id, shown behind a "Details" disclosure. */
  requestId?: string | null;
};

const ToastProvider = ToastPrimitive.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Viewport
    ref={ref}
    className={cn(
      // TOP-RIGHT, and `flex-col` so the newest sits at the bottom of the stack
      // and existing toasts do not jump downward as one arrives.
      'fixed right-0 top-0 z-[60] flex max-h-screen w-full flex-col items-end gap-2 p-4 sm:max-w-[340px]',
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitive.Viewport.displayName;

/**
 * Icon and wash per variant.
 *
 * The wash is the accent at 14% - the same ratio the mobile card uses - so the
 * chip reads as tinted rather than as a second solid colour.
 */
const VARIANTS: Record<ToastVariant, { icon: JSX.Element; wash: string }> = {
  success: {
    icon: <CheckCircle2 className="size-[15px] text-success" aria-hidden />,
    wash: 'bg-success/[0.14]',
  },
  error: {
    icon: <XCircle className="size-[15px] text-destructive" aria-hidden />,
    wash: 'bg-destructive/[0.14]',
  },
  warning: {
    icon: <AlertCircle className="size-[15px] text-warning" aria-hidden />,
    wash: 'bg-warning/[0.14]',
  },
  info: {
    icon: <Info className="size-[15px] text-primary" aria-hidden />,
    wash: 'bg-primary/[0.14]',
  },
};

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Root> & { variant: ToastVariant }
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Root
    ref={ref}
    className={cn(
      'group pointer-events-auto relative flex w-auto max-w-[300px] items-start gap-2.5',
      'rounded-[14px] border border-[#111827]/[0.06] bg-background/90 p-2.5 pr-3 shadow-overlay backdrop-blur-xl',
      // Enters from the right, leaves upward - the same motion as the phone.
      'data-[state=open]:animate-in data-[state=open]:slide-in-from-right-full data-[state=open]:fade-in-0',
      'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-top-2 data-[state=closed]:fade-out-0',
      'data-[swipe=end]:animate-out data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none',
      className,
    )}
    {...props}
  />
));
Toast.displayName = ToastPrimitive.Root.displayName;

function ToastIcon({ variant }: { variant: ToastVariant }): JSX.Element {
  return (
    <span
      className={cn(
        'mt-px flex size-6 shrink-0 items-center justify-center rounded-lg',
        VARIANTS[variant].wash,
      )}
      aria-hidden
    >
      {VARIANTS[variant].icon}
    </span>
  );
}

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Title
    ref={ref}
    className={cn('text-[12.5px] font-bold leading-4 text-foreground', className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitive.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Description
    ref={ref}
    className={cn('text-[11px] font-medium leading-[15px] text-muted-foreground', className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitive.Description.displayName;

/**
 * Click-anywhere-to-dismiss, matching the phone's tap gesture.
 *
 * Rendered as a full-cover transparent button rather than a visible ✕, so the
 * card stays as clean as the mobile one while remaining keyboard-reachable and
 * announced. It sits BELOW the text in z-order so a "Details" disclosure inside
 * the toast is still clickable.
 */
const ToastDismissSurface = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Close
    ref={ref}
    aria-label="Dismiss notification"
    className={cn(
      'absolute inset-0 z-0 rounded-[14px]',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
));
ToastDismissSurface.displayName = 'ToastDismissSurface';

export {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastIcon,
  ToastTitle,
  ToastDescription,
  ToastDismissSurface,
};
